import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, LogIn, Car, Bike, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, platePlaceholder } from '../../lib/parkingUtils';
import ReceiptImageInput from '../ReceiptImageInput';
import { getParkingPrice } from '../../lib/parkingPrices';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ParkingEntryDialog({
  open,
  onOpenChange,
  initialPlate = '',
  initialVehicleType = 'auto',
  tariff,
  eventTypes = [],
  entryType = 'kiosk',
  onSuccess,
}) {
  const [plate, setPlate] = useState(initialPlate);
  const [vehicleType, setVehicleType] = useState(initialVehicleType);
  const [addonEnabled, setAddonEnabled] = useState(false);
  const [addonEventId, setAddonEventId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [receiptImage, setReceiptImage] = useState(null);

  const addonMode = tariff?.event_addon_mode || 'disabled';
  const addonAvailable = addonMode !== 'disabled' && eventTypes.length > 0;
  const addonRequired = addonMode === 'required';
  const addonLabel = tariff?.event_addon_label || 'Activar evento';

  useEffect(() => {
    if (open) {
      setPlate(initialPlate);
      setVehicleType(initialVehicleType);
      setReceiptImage(null);
      if (addonRequired) {
        setAddonEnabled(true);
        setAddonEventId(tariff?.event_addon_default_id || '');
      } else {
        setAddonEnabled(false);
        setAddonEventId(tariff?.event_addon_default_id || '');
      }
    }
  }, [open, initialPlate, initialVehicleType, addonRequired, tariff?.event_addon_default_id]);

  const ev = eventTypes.find((e) => e.id === addonEventId);
  const parkingNow = getParkingPrice(vehicleType, tariff);
  const eventNow = (addonEnabled || addonRequired) && ev ? (ev.price || 0) : 0;
  const totalNow = (parkingNow || 0) + eventNow;

  const handleConfirm = async () => {
    if (!plate.trim()) {
      toast.error('Ingresa una patente');
      return;
    }
    if (addonRequired && !addonEventId) {
      toast.error('La configuracion exige seleccionar un evento');
      return;
    }
    if (addonEnabled && !addonRequired && !addonEventId) {
      toast.error('Selecciona el tipo de evento');
      return;
    }
    setConfirming(true);
    try {
      const payload = {
        plate: plate.toUpperCase(),
        entry_type: entryType,
        vehicle_type: vehicleType,
        service_type: 'parking',
        receipt_image: receiptImage,
      };
      if ((addonEnabled || addonRequired) && addonEventId) {
        payload.addon_event_type_id = addonEventId;
      }
      const { data } = await axios.post(`${API}/vehicles/entry`, payload, { withCredentials: true });
      const paidLabel = data.paid_at_entry ? ` — Cobrado: ${formatCurrency(data.amount_charged)}` : '';
      toast.success(`Entrada: ${data.plate}${data.is_member ? ' (SOCIO)' : ''}${paidLabel}`);
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar entrada');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[92vh] overflow-y-auto" data-testid="kiosk-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <LogIn className="w-5 h-5 text-emerald-400" />
            Registrar entrada
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-slate-300 text-sm mb-2 block">Patente</label>
            <Input
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder={platePlaceholder(vehicleType)}
              className="bg-slate-700 border-slate-600 text-white text-2xl font-mono text-center h-14 uppercase"
              autoFocus
              data-testid="kiosk-entry-plate"
            />
          </div>

          <div>
            <label className="text-slate-300 text-sm mb-2 block">Tipo de vehiculo</label>
            <Select value={vehicleType} onValueChange={setVehicleType}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="kiosk-vehicle-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="auto">
                  <span className="flex items-center gap-2"><Car className="w-4 h-4" />Auto</span>
                </SelectItem>
                <SelectItem value="moto">
                  <span className="flex items-center gap-2"><Bike className="w-4 h-4" />Moto</span>
                </SelectItem>
              </SelectContent>
            </Select>
            {vehicleType === 'moto' && (
              <p className="text-[11px] text-amber-300/80 mt-1">
                Formatos: A123ABC (actual) o ABC123 (anterior). En ABC123 elegi Moto para distinguir de auto.
              </p>
            )}
          </div>

          {addonAvailable && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-900/10 p-3">
              <button
                type="button"
                onClick={() => !addonRequired && setAddonEnabled((v) => !v)}
                disabled={addonRequired}
                className="w-full flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold">{addonLabel}</span>
                </div>
                <Badge className={(addonEnabled || addonRequired) ? 'bg-amber-500' : 'bg-slate-600'}>
                  {(addonEnabled || addonRequired) ? 'ON' : 'OFF'}
                </Badge>
              </button>
              {(addonEnabled || addonRequired) && (
                <Select value={addonEventId} onValueChange={setAddonEventId}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-2 h-10">
                    <SelectValue placeholder="Seleccionar evento..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {eventTypes.map((et) => (
                      <SelectItem key={et.id} value={et.id}>
                        {et.name} — {formatCurrency(et.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {(parkingNow > 0 || eventNow > 0) && (
            <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-3">
              <div className="flex justify-between text-sm text-slate-300">
                <span>Estacionamiento</span>
                <span className="font-mono">
                  {formatCurrency(parkingNow)}
                </span>
              </div>
              {eventNow > 0 && (
                <div className="flex justify-between text-sm text-slate-300 mt-1">
                  <span>Evento</span>
                  <span className="font-mono text-amber-300">{formatCurrency(eventNow)}</span>
                </div>
              )}
              <div className="flex justify-between mt-2 pt-2 border-t border-slate-700">
                <span className="text-xs uppercase text-slate-400">Total ahora</span>
                <span className="text-xl font-bold text-emerald-400 font-mono">
                  {formatCurrency(totalNow)}
                </span>
              </div>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm text-slate-300">Comprobante de pago (opcional)</label>
            <ReceiptImageInput value={receiptImage} onChange={setReceiptImage} disabled={confirming} />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300" disabled={confirming}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={confirming || ((addonEnabled || addonRequired) && !addonEventId)}
            className="bg-emerald-600 hover:bg-emerald-700 font-semibold"
            data-testid="kiosk-confirm-entry-btn"
          >
            {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Confirmar entrada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
